import styled, { keyframes } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../components/AppShell/styles";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

const pop = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  70% {
    opacity: 1;
    transform: scale(1.06);
  }
  100% {
    transform: scale(1);
  }
`;

/* The checkout's look: dark, the same soft green glow on top, Inter */
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100dvh - var(--app-bottom-nav-height, 0px));
  background:
    radial-gradient(60% 360px at 50% 0%, rgba(87, 242, 139, 0.12) 0%, rgba(87, 242, 139, 0) 70%),
    var(--color-neutral-darkness);

  &,
  & * {
    font-family: Inter, sans-serif;
  }
`;

/* The checkout's narrow column, centered */
export const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 520px;
  padding: 4.5rem 0 2rem;
  margin: 0 auto;

  @media ${MOBILE} {
    padding: 3rem 20px 1.5rem;
  }
`;

export const Hero = styled.header`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  align-items: center;
  text-align: center;

  .check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    color: var(--color-neutral-darkness);
    background: var(--color-brand-plus);
    border-radius: 50%;
    box-shadow: 0 0 0 10px rgba(87, 242, 139, 0.12);
    /* Room for its glow ring before the tag */
    margin-bottom: 10px;
    animation: ${pop} 0.5s ease-out both;

    svg {
      width: 34px;
      height: 34px;
    }

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  h1 {
    margin-top: 0.25rem;
    font-size: 1.75rem;
    font-weight: 800;
    line-height: 2.25rem;
    color: var(--color-neutral-white);
    letter-spacing: -0.01em;

    @media ${MOBILE} {
      font-size: 1.5rem;
      line-height: 2rem;
    }
  }

  p {
    max-width: 420px;
    font-size: 0.9375rem;
    line-height: 1.5rem;
    color: var(--color-neutral-150);
  }
`;

/* The subscription, as a receipt: label on the left, value on the right */
export const Receipt = styled.dl`
  display: flex;
  flex-direction: column;
  padding: 0.5rem 1.25rem;
  background: var(--color-neutral-dark);
  border: 1px solid var(--color-neutral-base);
  border-radius: 1rem;

  > div {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 0;

    & + div {
      border-top: 1px solid var(--color-neutral-base);
    }
  }

  dt {
    font-size: 0.8125rem;
    color: var(--color-neutral-150);
  }

  dd {
    min-width: 0;
    overflow: hidden;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .total dd {
    font-size: 1.125rem;
    font-weight: 800;
  }
`;

/* A boleto not paid yet: the PLUS is on, but it ends if the boleto expires */
export const Note = styled.p`
  padding: 0.875rem 1rem;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  color: var(--color-neutral-lightness);
  background: rgba(255, 122, 0, 0.12);
  border: 1px solid rgba(255, 122, 0, 0.45);
  border-radius: 0.875rem;

  b {
    font-weight: 700;
    color: var(--color-neutral-white);
  }
`;

export const Unlocked = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  h2 {
    font-size: 0.75rem;
    font-weight: 800;
    line-height: 1rem;
    color: var(--color-neutral-150);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  ul {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.625rem;
  }

  li {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.875rem 1rem;
    background: var(--color-neutral-dark);
    border-radius: 0.875rem;
  }

  .icon {
    font-size: 1.125rem;
    line-height: 1.5rem;
  }

  b {
    font-size: 0.8125rem;
    font-weight: 700;
    line-height: 1.125rem;
    color: var(--color-neutral-white);
  }

  span.text {
    font-size: 0.75rem;
    line-height: 1.0625rem;
    color: var(--color-neutral-150);
  }
`;

/* The actions, pinned to the bottom (as the other screens' bars): the button, and the links under it */
export const Actions = styled.div`
  position: sticky;
  bottom: var(--app-bottom-bar-offset, 0px);
  z-index: 50;
  padding: 1rem 0 calc(1rem + env(safe-area-inset-bottom));
  background: var(--color-neutral-darkness);
  border-top: 1px solid var(--color-neutral-dark);

  @media ${MOBILE} {
    padding-right: 20px;
    padding-left: 20px;
  }

  .inner {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
    align-items: center;
    width: 100%;
    max-width: 520px;
    margin: 0 auto;

    > div {
      width: 100%;
    }
  }

  .links {
    display: flex;
    gap: 1.25rem;
    justify-content: center;
  }

  .links button {
    font-size: 0.8125rem;
    font-weight: 700;
    color: var(--color-neutral-150);
    text-decoration: underline;
    text-underline-offset: 3px;
    transition: color 0.15s;

    &:hover {
      color: var(--color-neutral-white);
    }
  }
`;
